// Setup › Price book on ui-web's PricebookView (DEC-019, Blueprint): the company's book as the page
// draws it. Each item read (pricebook.listItems, pricebook.getItem) becomes the page's row and its
// sheet; a sheet as typed becomes pricebook.updateItem's or createItem's input, sending only what
// changed; and Edit them together becomes one update per item. What the book doesn't keep (techs'
// asks, customer words beyond "What it includes", sales counts) isn't drawn: no filter, column or
// section stands for it.

import { discounted, pictureShownFirst, type ArtTheme, type PictureFirst } from '@flowology/domain';
import { money } from '@flowology/ui-web/fl';
import type {
  BookCategory,
  BookColumn,
  BookFilter,
  BookItem,
  BulkField,
  SheetItem,
  SheetViewer,
  SheetWords,
} from '@flowology/ui-web/pricebook';
import { categoryOptions, type Category } from '../inventory/catalog-model.ts';
import { withReason } from './item-history-model.ts';
import { centsToDollars, dollarsToCents } from './settings-model.ts';
import {
  ITEM_TYPES,
  TAX_CLASSES,
  itemForm,
  itemInput,
  newItemInput,
  type Built,
  type Item,
} from './setup-model.ts';

/** Who looks: the owner (or whoever they let see our cost), the office who edit, or one who reads. */
export function viewerOf(held: readonly string[]): SheetViewer {
  if (!held.includes('pricebook.edit')) return 'csr';
  return held.includes('pricebook.cost.view') ? 'owner' : 'office';
}

/** The filters the book can tell: no picture, and for who approves them, pictures waiting; no words. */
export const filtersFor = (canEdit: boolean): BookFilter[] =>
  canEdit ? ['nopic', 'toapprove', 'nowords'] : ['nopic', 'nowords'];
export const COLUMNS: BookColumn[] = ['where', 'price', 'member', 'words'];

/** An item's picture in light and dark: the one each shows first (domain's rule), its thumbnail. */
export function pictureSrc(
  i: Pick<Item, 'art' | 'picture'>,
  first: PictureFirst,
): { light: string; dark: string } | null {
  const at = (t: ArtTheme) => {
    const p = pictureShownFirst(i, t, first);
    return p ? (p.thumbUrl ?? ('url' in p ? p.url : null)) : null;
  };
  const light = at('light');
  const dark = at('dark') ?? light;
  if (!light && !dark) return null;
  return { light: light ?? dark!, dark: dark ?? light! };
}

/** What it includes, as the sheet's words: one field, a line each. */
export function wordsOf(description: string | null): SheetWords {
  return {
    state: 'approved',
    name: '',
    what: '',
    included: (description ?? '').split('\n'),
    warranty: null,
    fields: ['included'],
  };
}

/** A row of the page: its picture (or its new one waiting), and whether it says what it includes. */
export function bookItemOf(i: Item, waiting: ReadonlySet<string>, first: PictureFirst): BookItem {
  const picture = pictureSrc(i, first);
  return {
    id: i.id,
    categoryId: i.categoryId,
    code: i.code,
    name: i.name,
    priceCents: i.priceCents,
    memberPriceCents: i.memberPriceCents,
    picture,
    artState: picture ? 'ready' : waiting.has(i.id) ? 'to_approve' : 'none',
    words: i.description?.trim() ? wordsOf(i.description) : null,
    active: i.active,
  };
}

export const bookCategories = (cats: Category[]): BookCategory[] =>
  cats.filter((c) => c.active).map((c) => ({ id: c.id, name: c.name, parentId: c.parentId }));

/** Where an item sits, by name: "Water heaters › Tank", up to three deep. */
export function pathOf(cats: readonly Category[], id: string | null): string {
  const by = new Map(cats.map((c) => [c.id, c]));
  const out: string[] = [];
  for (
    let c = id ? by.get(id) : undefined;
    c && out.length < 3;
    c = c.parentId ? by.get(c.parentId) : undefined
  )
    out.unshift(c.name);
  return out.join(' › ');
}

/** Where an item can move to: none, or a category in use. */
export const placeOptions = (cats: Category[]) => [
  { value: '', label: 'Not in a category' },
  ...categoryOptions(cats).map((c) => ({ value: c.id, label: c.label })),
];
export const TAX_OPTIONS = TAX_CLASSES.map(([value, label]) => ({ value, label }));
export const KIND_OPTIONS = ITEM_TYPES.map(([value, label]) => ({ value, label }));
const kindWord = (type: string) => ITEM_TYPES.find((t) => t[0] === type)?.[1] ?? type;

/** The owner's margin line, when our cost is in: what's left of the price. */
export function marginOf(i: Pick<Item, 'priceCents' | 'costCents'>): SheetItem['margin'] {
  if (i.costCents == null || !i.priceCents) return undefined;
  const m = Math.round(((i.priceCents - i.costCents) / i.priceCents) * 100);
  return { text: `Margin ${m} %`, ...(m < 0 ? { isBad: true } : {}) };
}

/** An item's sheet as read: list row or, once it's read, the item itself. */
export function sheetOfItem(i: Item, cats: readonly Category[], first: PictureFirst): SheetItem {
  const picture = pictureSrc(i, first);
  return {
    code: i.code,
    kind: kindWord(i.type),
    name: i.name,
    picture: { src: picture, state: picture ? 'ready' : 'none', line: '' },
    words: wordsOf(i.description),
    price: centsToDollars(i.priceCents),
    member: i.memberPriceCents === null ? '' : centsToDollars(i.memberPriceCents),
    ...(i.costCents === undefined
      ? {}
      : { cost: i.costCents === null ? '' : centsToDollars(i.costCents), margin: marginOf(i) }),
    madeOf: [],
    place: { path: pathOf(cats, i.categoryId), categoryId: i.categoryId ?? '' },
    history: [],
    sell: { unit: i.unit, taxable: i.taxable, taxClass: i.taxClass, active: i.active },
    reason: '',
  };
}

/** A new item's sheet: no name, code or price yet; sold each, taxed at the standard rate. */
export function newSheet(seesCost: boolean): SheetItem {
  return {
    code: '',
    kind: 'service',
    name: '',
    picture: { src: null, state: 'none', line: '' },
    words: wordsOf(null),
    price: '',
    member: '',
    ...(seesCost ? { cost: '' } : {}),
    madeOf: [],
    place: { path: '', categoryId: '' },
    history: [],
    sell: { unit: 'each', taxable: true, taxClass: 'standard', active: true },
  };
}

const included = (s: SheetItem) => (s.words?.included ?? []).join('\n');

/** pricebook.updateItem's input from the sheet as typed: only what changed, with the why. */
export function saveInput(i: Item, s: SheetItem): Built {
  const was = itemForm(i);
  const sell = s.sell ?? { unit: was.unit, taxable: was.taxable, taxClass: was.taxClass, active: was.active };
  const built = itemInput(i, {
    name: s.name,
    description: included(s),
    unit: sell.unit,
    price: s.price,
    memberPrice: s.member,
    cost: s.cost ?? was.cost,
    taxable: sell.taxable,
    taxClass: sell.taxClass,
    categoryId: s.place.categoryId ?? was.categoryId,
    active: sell.active,
  });
  return withReason(built, s.reason ?? '');
}

/** pricebook.createItem's input from a new item's sheet. */
export function newInput(s: SheetItem): Built {
  const sell = s.sell ?? { unit: 'each', taxable: true, taxClass: 'standard', active: true };
  const built = newItemInput({
    type: s.kind,
    code: s.code,
    name: s.name,
    unit: sell.unit,
    price: s.price,
    memberPrice: s.member,
    taxable: sell.taxable,
    categoryId: s.place.categoryId ?? '',
  });
  if ('error' in built) return built;
  const words = included(s).trim();
  let cost: number | null = null;
  if (s.cost?.trim()) {
    cost = dollarsToCents(s.cost);
    if (cost === null) return { error: 'Our cost doesn’t look like an amount. Type it like 129.00.' };
  }
  return {
    input: {
      ...built.input,
      ...(words ? { description: words } : {}),
      ...(sell.taxable ? { taxClass: sell.taxClass } : {}),
      ...(cost !== null ? { costCents: cost } : {}),
    },
  };
}

/** Edit them together's fields: where they sit, the price, what members pay, and whether offered. */
export function bulkFields(cats: Category[]): BulkField[] {
  return [
    { key: 'where', label: 'Where they sit', options: placeOptions(cats).slice(1) },
    {
      key: 'price',
      label: 'Price',
      options: [
        { value: 'up5', label: 'Up 5 %' },
        { value: 'up10', label: 'Up 10 %' },
        { value: 'down5', label: 'Down 5 %' },
      ],
    },
    {
      key: 'member',
      label: 'Members pay',
      options: [
        { value: 'm10', label: '10 % off the price' },
        { value: 'discount', label: 'The member discount' },
      ],
    },
    {
      key: 'sale',
      label: 'For sale',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No: retired' },
      ],
    },
  ];
}

/** Price ±%: the step in percent. */
const STEP: Record<string, number> = { up5: 5, up10: 10, down5: -5 };

/**
 * A price moved by a percent, rounded half up to the cent: the domain's rule for a percent off a price
 * (discounted(), in basis points, whole cents as the book stores them), here either way.
 */
export function byPercent(cents: number, pct: number): number {
  if (pct < 0) return discounted(cents, String(-pct));
  const bp = Math.round(pct * 100);
  return Math.floor((cents * (10_000 + bp) + 5_000) / 10_000);
}

/** One item's pricebook.updateItem input for Edit them together; null when nothing changes it. */
export function bulkInput(i: Item, changes: Record<string, string>): Built | null {
  const out: Record<string, unknown> = {};
  if (changes.where !== undefined && changes.where !== (i.categoryId ?? ''))
    out.categoryId = changes.where || null;
  const step = changes.price ? STEP[changes.price] : undefined;
  const price = step === undefined ? i.priceCents : byPercent(i.priceCents, step);
  if (price !== i.priceCents) out.priceCents = price;
  if (changes.member === 'm10') {
    const m = discounted(price, '10');
    if (m !== i.memberPriceCents) out.memberPriceCents = m;
  } else if (changes.member === 'discount' && i.memberPriceCents !== null) out.memberPriceCents = null;
  if (changes.sale === 'yes' && !i.active) out.active = true;
  if (changes.sale === 'no' && i.active) out.active = false;
  if (!Object.keys(out).length) return null;
  return {
    input: { id: i.id, rowVersion: i.rowVersion, ...out, reason: 'Changed with others in the price book' },
  };
}

/** What Edit them together would do to the ticked items, before anything is sent. */
export interface BulkPlan {
  /** The items it changes, each with its update. */
  changes: { item: Item; input: Record<string, unknown> }[];
  /** Already that way: nothing to send. */
  same: Item[];
  /** Read only here (imported while the company mirrors): skipped, never sent. */
  skipped: Item[];
}

/**
 * `kept`: the updates a run of the same changes didn't get in, as first built. A retry sends those again,
 * never one built from a fresh read: an update that timed out but went in has moved the price, and a
 * percent built on it would apply twice. Sent again, its first read's rowVersion is refused instead.
 */
export function bulkPlan(
  items: readonly Item[],
  changes: Record<string, string>,
  editable: (i: Item) => boolean,
  kept?: ReadonlyMap<string, BulkPlan['changes'][number]>,
): BulkPlan {
  const plan: BulkPlan = { changes: [], same: [], skipped: [] };
  for (const i of items) {
    if (!editable(i)) {
      plan.skipped.push(i);
      continue;
    }
    const first = kept?.get(i.id);
    if (first) {
      plan.changes.push(first);
      continue;
    }
    const built = bulkInput(i, changes);
    if (!built || 'error' in built) plan.same.push(i);
    else plan.changes.push({ item: i, input: built.input });
  }
  return plan;
}

const items = (n: number) => `${n} ${n === 1 ? 'item' : 'items'}`;
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const names = (xs: readonly Item[]) => xs.map((i) => i.name).join(', ');

/** The preview over Change: "12 items: $1,450.00 → $1,595.00", one line per item, what's skipped. */
export function bulkPreview(plan: BulkPlan, cats: readonly Category[]): { lines: string[]; each: string[] } {
  const lines: string[] = [];
  const priced = plan.changes.filter((c) => c.input.priceCents !== undefined);
  if (priced.length)
    lines.push(
      `${items(priced.length)}: ${money(sum(priced.map((c) => c.item.priceCents)))} → ${money(
        sum(priced.map((c) => c.input.priceCents as number)),
      )}`,
    );
  const member = plan.changes.filter((c) => c.input.memberPriceCents !== undefined);
  if (member.length) {
    const to = member.filter((c) => c.input.memberPriceCents !== null);
    lines.push(
      to.length
        ? `Members pay, ${items(to.length)}: ${money(sum(to.map((c) => c.input.memberPriceCents as number)))}`
        : `Members pay, ${items(member.length)}: the member discount`,
    );
  }
  const moved = plan.changes.filter((c) => c.input.categoryId !== undefined);
  if (moved.length) {
    const to = moved[0]!.input.categoryId as string | null;
    lines.push(`${items(moved.length)} move to ${to ? pathOf(cats, to) : 'no category'}`);
  }
  const sale = plan.changes.filter((c) => c.input.active !== undefined);
  if (sale.length) lines.push(`${items(sale.length)} ${sale[0]!.input.active ? 'offered again' : 'retired'}`);
  if (plan.same.length) lines.push(`${items(plan.same.length)} already that way`);
  if (plan.skipped.length)
    lines.push(`${items(plan.skipped.length)} skipped, read only here: ${names(plan.skipped)}`);
  if (!plan.changes.length) lines.push('Nothing to change.');
  const each = plan.changes.map(({ item, input }) => {
    const said: string[] = [];
    if (input.priceCents !== undefined)
      said.push(`${money(item.priceCents)} → ${money(input.priceCents as number)}`);
    if (input.memberPriceCents !== undefined)
      said.push(
        `members ${item.memberPriceCents === null ? 'discount' : money(item.memberPriceCents)} → ${
          input.memberPriceCents === null ? 'discount' : money(input.memberPriceCents as number)
        }`,
      );
    if (input.categoryId !== undefined) said.push('moves');
    if (input.active !== undefined) said.push(input.active ? 'offered again' : 'retired');
    return `${item.name}: ${said.join(', ')}`;
  });
  return { lines, each };
}

/**
 * Runs the plan, one update per item, in order. A refusal never stops the rest and is never silent:
 * what changed stays changed, and each item that didn't is named with its reason, its id kept so it
 * stays ticked to try again. One the server never answered may have gone in, so its update is kept as
 * built, for bulkPlan's retry; one it refused didn't, so a retry builds it again from a fresh read.
 */
export async function applyBulk(
  plan: BulkPlan,
  send: (input: Record<string, unknown>) => Promise<unknown>,
  reasonOf: (e: unknown) => string,
  unanswered: (e: unknown) => boolean,
): Promise<{ said: string; retry: string[]; kept: BulkPlan['changes'] }> {
  let done = 0;
  const failed: { item: Item; reason: string }[] = [];
  const kept: BulkPlan['changes'] = [];
  for (const change of plan.changes) {
    try {
      await send(change.input);
      done += 1;
    } catch (e) {
      failed.push({ item: change.item, reason: reasonOf(e) });
      if (unanswered(e)) kept.push(change);
    }
  }
  return {
    said: bulkWords(done, plan.same.length, plan.skipped, failed),
    retry: failed.map((f) => f.item.id),
    kept,
  };
}

/** What Edit them together did, in words: "9 changed, 3 didn't: …". */
export function bulkWords(
  changed: number,
  unchanged: number,
  skipped: readonly Item[],
  failed: readonly { item: Item; reason: string }[],
): string {
  const said: string[] = [];
  if (failed.length)
    said.push(
      `${changed} changed, ${failed.length} didn’t: ${failed.map((f) => `${f.item.name} (${f.reason.trim().replace(/\.$/, '')})`).join('; ')}.`,
    );
  else if (changed) said.push(`${items(changed)} changed.`);
  if (unchanged) said.push(`${unchanged} already ${unchanged === 1 ? 'was' : 'were'} that way.`);
  if (skipped.length) said.push(`${skipped.length} skipped, read only here: ${names(skipped)}.`);
  return said.join(' ') || 'Nothing changed.';
}
